/**
 * «Следующий год» (core-loop-v2 §2, такт 4): всегда внизу колонки.
 * Тап — `game.endYear()`. Пока решение, мини-игра или предложенная мечта
 * ждут ответа, кнопка `disabled`, причина в её тексте («Следующий год —
 * сначала ответь»), а карточка, которая ждёт, подсвечена (`attention`).
 * Долгое нажатие LONG_PRESS_MS — автоповтор AUTO_REPEAT_MS (4 года в
 * секунду), пока держат палец и пока год идёт: решение, мини-игра или
 * смерть останавливают повтор (`endYear` отвечает false). Клик, которым
 * заканчивается удержание, глотается — иначе одно касание дало бы лишний год.
 */
import { t } from '@life/i18n';
import { useEffect, useRef } from 'preact/hooks';
import { LONG_PRESS_MS } from '../lib/press';
import type { Game } from '../state/game';

/** Интервал автоповтора при удержании: 4 года в секунду. */
export const AUTO_REPEAT_MS = 250;

export function NextYearButton({ game }: { game: Game }) {
  const timers = useRef<{ press: ReturnType<typeof setTimeout> | null; repeat: ReturnType<typeof setInterval> | null; fired: boolean }>({
    press: null,
    repeat: null,
    fired: false,
  });

  const stop = () => {
    const s = timers.current;
    if (s.press !== null) clearTimeout(s.press);
    if (s.repeat !== null) clearInterval(s.repeat);
    s.press = null;
    s.repeat = null;
  };
  useEffect(() => stop, []);

  const life = game.life.value;
  const alive = life?.alive ?? false;
  const dreamWaits = life !== null && life.pendingDream !== null && life.pendingDream.length > 0;
  const blocked =
    !alive || (!game.autopilot.value && (game.pending.value !== null || game.pendingMinigame.value !== null || dreamWaits));

  const step = () => {
    if (!game.endYear()) stop();
  };

  return (
    <div class="next-year">
      <button
        type="button"
        class="btn primary block"
        disabled={blocked}
        onPointerDown={(e) => {
          if (blocked || (e.button !== undefined && e.button !== 0)) return;
          stop();
          timers.current.fired = false;
          timers.current.press = setTimeout(() => {
            timers.current.press = null;
            timers.current.fired = true;
            step();
            if (timers.current.fired) timers.current.repeat = setInterval(step, AUTO_REPEAT_MS);
          }, LONG_PRESS_MS);
        }}
        onPointerUp={stop}
        onPointerLeave={stop}
        onPointerCancel={stop}
        onContextMenu={(e) => e.preventDefault()}
        onClick={(e) => {
          if (timers.current.fired) {
            timers.current.fired = false;
            e.preventDefault();
            return;
          }
          if (!blocked) game.endYear();
        }}
      >
        {blocked && alive ? t('ui.next.blocked') : t('ui.next.year')}
      </button>
      <p class="hint next-hint">{t('ui.next.hold')}</p>
    </div>
  );
}
